import { api, ApiError } from '@/lib/api';
import type {
  WikiChatDone,
  WikiChatRequest,
  WikiPage,
  WikiSearchResponse,
  WikiSource,
  WikiStreamHandlers,
  WikiSyncStatus,
} from './types';

const WIKI_BASE = '/v4/wiki';

interface ParserState {
  event: string;
  dataLines: string[];
}

function parseJsonData(raw: string): unknown {
  if (!raw.trim()) return {};
  return JSON.parse(raw);
}

function dispatchEvent(state: ParserState, handlers: WikiStreamHandlers) {
  if (state.dataLines.length === 0) return;
  const rawData = state.dataLines.join('\n');
  const payload = parseJsonData(rawData);

  if (state.event === 'sources') {
    const sources = typeof payload === 'object' && payload !== null && 'sources' in payload
      ? (payload as { sources?: WikiSource[] }).sources ?? []
      : [];
    handlers.onSources?.(sources);
  } else if (state.event === 'token') {
    const text = typeof payload === 'object' && payload !== null && 'text' in payload
      ? String((payload as { text?: unknown }).text ?? '')
      : String(payload ?? '');
    if (text) handlers.onToken?.(text);
  } else if (state.event === 'done') {
    handlers.onDone?.((payload || {}) as WikiChatDone);
  } else if (state.event === 'error') {
    const message = typeof payload === 'object' && payload !== null && 'error' in payload
      ? String((payload as { error?: unknown }).error ?? 'ATEK Brain error')
      : String(payload || 'ATEK Brain error');
    handlers.onError?.(message);
  }
}

export function createWikiSseParser(handlers: WikiStreamHandlers) {
  let buffer = '';
  const state: ParserState = {
    event: 'message',
    dataLines: [],
  };

  function resetEvent() {
    state.event = 'message';
    state.dataLines = [];
  }

  function processLine(line: string) {
    const trimmed = line.endsWith('\r') ? line.slice(0, -1) : line;
    if (trimmed === '') {
      dispatchEvent(state, handlers);
      resetEvent();
      return;
    }
    if (trimmed.startsWith('event:')) {
      state.event = trimmed.slice(6).trim();
      return;
    }
    if (trimmed.startsWith('data:')) {
      state.dataLines.push(trimmed.slice(5).trimStart());
    }
  }

  return {
    push(chunk: string) {
      buffer += chunk;
      const endsWithNewline = buffer.endsWith('\n');
      const lines = buffer.split('\n');
      buffer = endsWithNewline ? '' : lines.pop() ?? '';
      for (const line of lines) processLine(line);
    },
    flush() {
      if (buffer) {
        processLine(buffer);
        buffer = '';
      }
      dispatchEvent(state, handlers);
      resetEvent();
    },
  };
}

export const wikiAssistantKeys = {
  all: ['wiki-assistant'] as const,
  status: () => [...wikiAssistantKeys.all, 'status'] as const,
  search: (query: string, sections?: string[]) => [...wikiAssistantKeys.all, 'search', query, sections] as const,
  page: (wikiPath: string) => [...wikiAssistantKeys.all, 'page', wikiPath] as const,
};

export function getWikiSyncStatus(): Promise<WikiSyncStatus> {
  return api.get<WikiSyncStatus>(`${WIKI_BASE}/sync-status`);
}

export function searchWiki(query: string, sections?: string[]): Promise<WikiSearchResponse> {
  return api.get<WikiSearchResponse>(`${WIKI_BASE}/search`, {
    q: query,
    sections: sections?.join(','),
  });
}

export function getWikiPage(wikiPath: string): Promise<WikiPage> {
  return api.get<WikiPage>(`${WIKI_BASE}/pages`, { path: wikiPath });
}

export function syncWiki(): Promise<WikiSyncStatus> {
  return api.post<WikiSyncStatus>(`${WIKI_BASE}/sync`, {});
}

export async function streamWikiChat(
  request: WikiChatRequest,
  handlers: WikiStreamHandlers,
  signal?: AbortSignal,
) {
  const response = await fetch('/api/v4/wiki/chat', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
    signal,
  });

  if (response.status === 401) {
    const currentPath = window.location.pathname + window.location.search;
    window.location.href = `/login?url=${encodeURIComponent(currentPath)}`;
    throw new ApiError('Session expired', 401);
  }

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new ApiError(text || `ATEK Brain error: ${response.status}`, response.status, text);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new ApiError('ATEK Brain returned no response stream', response.status);

  const decoder = new TextDecoder();
  const parser = createWikiSseParser(handlers);

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    parser.push(decoder.decode(value, { stream: true }));
  }
  parser.push(decoder.decode());
  parser.flush();
}
